import React, { useState } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import { HiOutlinePencil } from 'react-icons/hi';
import RoadmapDrawer from './RoadmapDrawer';

const STATUS_COLORS = {
  'Completed':   'green',
  'In Progress': 'blue',
  'At Risk':     'red',
  'Not Started': 'gray',
  'On Hold':     'yellow',
  'Blocked':     'red'
};
const RISK_COLORS = { High: 'red', Medium: 'yellow', Low: 'green' };

function daysUntil(date) {
  const diff = new Date(date) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function CountdownLabel({ date, status }) {
  if (status === 'Completed') return <span className="v26-countdown v26-countdown--past">Completed</span>;
  const days = daysUntil(date);
  if (days < 0)  return <span className="v26-countdown v26-countdown--past">{Math.abs(days)}d overdue</span>;
  if (days <= 14) return <span className="v26-countdown v26-countdown--urgent">{days}d left</span>;
  if (days <= 45) return <span className="v26-countdown v26-countdown--soon">{days}d left</span>;
  return <span className="v26-countdown v26-countdown--ok">{days}d left</span>;
}

function ProgressColor(pct) {
  if (pct >= 80) return '#10b981';
  if (pct >= 40) return '#a8336b';
  if (pct >= 10) return '#f59e0b';
  return '#ef4444';
}

// ─── Edit Mission Modal — head + team, status, risk, remarks ─────────────────
const EditMissionModal = ({ mission, onClose, onSave, systemUsers = [] }) => {
  const [form, setForm] = useState({
    head: mission.head?._id || mission.head || '',
    status: mission.status,
    riskLevel: mission.riskLevel,
    remarks: mission.remarks || '',
    targetDate: mission.targetDate ? new Date(mission.targetDate).toISOString().slice(0,10) : '',
    team: (mission.team || []).map(m => typeof m === 'object' ? m : { _id: m, fullName: m })
  });
  const [userSearch, setUserSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredUsers = userSearch.trim()
    ? systemUsers.filter(u =>
        (u.fullName || u.username).toLowerCase().includes(userSearch.toLowerCase()) &&
        !form.team.some(m => m._id === u._id)
      ).slice(0, 8)
    : [];

  const addMember = (user) => {
    if (!user || form.team.some(m => m._id === user._id)) { setUserSearch(''); return; }
    setForm(f => ({ ...f, team: [...f.team, { _id: user._id, fullName: user.fullName, username: user.username }] }));
    setUserSearch('');
  };
  const removeMember = (id) => setForm(f => ({ ...f, team: f.team.filter(m => m._id !== id) }));

  const handleSave = async () => {
    if (form.status === 'Completed' && mission.status !== 'Completed') {
      const ok = window.confirm(
        `Mark mission "${mission.name}" as Completed?\n\n` +
        `This will mark ALL goals, action items, tasks, and subtasks under this mission as Completed.`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        riskLevel: form.riskLevel,
        remarks: form.remarks,
        targetDate: form.targetDate,
        team: form.team.map(m => m._id),
        head: form.head || null
      };
      await axiosInstance.put(`/api/vision/missions/${mission._id}`, payload);
      onSave();
    } catch (err) { console.error(err); alert(err.response?.data?.message || 'Failed to update mission.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="v26-drawer-overlay" onClick={onClose}>
      <div className="v26-drawer" onClick={e => e.stopPropagation()}>
        <div className="v26-drawer-header" style={{ position: 'relative' }}>
          <h2>Edit Mission</h2>
          <p>{mission.name}</p>
          <button className="v26-drawer-close" onClick={onClose}>×</button>
        </div>
        <div className="v26-drawer-body">
          <div className="v26-form">

            <div className="v26-form-group">
              <label>Mission Head</label>
              <select value={form.head} onChange={e => setForm(f => ({ ...f, head: e.target.value }))}>
                <option value="">— None —</option>
                {systemUsers.map(u => <option key={u._id} value={u._id}>{u.fullName || u.username}</option>)}
              </select>
            </div>

            <div className="v26-form-group">
              <label>Team Members</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, minHeight: 32 }}>
                {form.team.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No team members yet</span>}
                {form.team.map(member => (
                  <span key={member._id} className="v26-team-chip">
                    {member.fullName}
                    <button className="v26-team-chip-remove" onClick={() => removeMember(member._id)}>×</button>
                  </span>
                ))}
              </div>
              <div style={{ position: 'relative' }}>
                <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                  placeholder="Search users to add…"
                  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 12px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }} />
                {filteredUsers.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, overflow: 'hidden', marginTop: 4 }}>
                    {filteredUsers.map(u => (
                      <div key={u._id} onClick={() => addMember(u)}
                        style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ fontWeight: 600 }}>{u.fullName}</span>
                        <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>@{u.username}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }} />

            <div className="v26-form-row">
              <div className="v26-form-group">
                <label>Target Date</label>
                <input type="date" value={form.targetDate} onChange={e => setForm(f => ({ ...f, targetDate: e.target.value }))} />
              </div>
              <div className="v26-form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {['Not Started','In Progress','Completed','At Risk','On Hold'].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="v26-form-group">
                <label>Risk</label>
                <select value={form.riskLevel} onChange={e => setForm(f => ({ ...f, riskLevel: e.target.value }))}>
                  {['Low','Medium','High'].map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div className="v26-form-group">
              <label>Completion — {mission.completionPercentage}%</label>
              <div style={{ background: '#f1f5f9', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                Auto-calculated from goals (weighted). Manage in the Roadmap.
              </div>
            </div>

            <div className="v26-form-group">
              <label>Remarks</label>
              <textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} rows={3} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="v26-btn v26-btn--secondary" onClick={onClose}>Cancel</button>
              <button className="v26-btn v26-btn--primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Mission Dashboard ───────────────────────────────────────────────────────
const MissionDashboard = ({ missions, stats, selectedYear, lockState = 'active', onMissionUpdate, onRollup, systemUsers = [], roadmapRefreshKey, user }) => {
  const [editMission, setEditMission] = useState(null);
  const [roadmapMissionId, setRoadmapMissionId] = useState(null);
  const [lockBusyId, setLockBusyId] = useState(null);

  const isAdmin = user?.role === 'system_admin';

  // Locking closes a phase: it freezes the mission and hides it from everyone
  // but admins. The follow-up phase is created as a separate mission.
  const toggleLock = async (mission) => {
    const locking = !mission.isLocked;
    let reason;
    if (locking) {
      const ok = window.confirm(
        `Lock mission "${mission.name}"?\n\n` +
        `• Nobody will be able to add or change anything inside it — including you.\n` +
        `• It will disappear for every user except system admins.\n` +
        `• Its progress and figures stay frozen exactly as they are.\n\n` +
        `You can unlock it again at any time.`
      );
      if (!ok) return;
      reason = window.prompt('Reason for locking (optional):', '') || undefined;
    } else if (!window.confirm(`Unlock mission "${mission.name}"? It becomes visible and editable again.`)) {
      return;
    }

    setLockBusyId(mission._id);
    try {
      await axiosInstance.patch(`/api/vision/missions/${mission._id}/lock`, { isLocked: locking, reason });
      onMissionUpdate();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to change the lock.');
    } finally {
      setLockBusyId(null);
    }
  };

  if (!missions.length) {
    if (lockState === 'locked') {
      return (
        <div className="v26-empty">
          No locked missions for {selectedYear}.<br />
          <span style={{ fontSize: 13 }}>Locked missions are closed phases — frozen, and hidden from everyone except system admins.</span>
        </div>
      );
    }
    if (!isAdmin) {
      return (
        <div className="v26-empty">
          You are not a team member of any mission for {selectedYear}.<br />
          <span style={{ fontSize: 13 }}>Ask a coordinator or admin to add you to a mission to see it here.</span>
        </div>
      );
    }
    return (
      <div className="v26-empty">
        No missions for {selectedYear}.<br />
        <span style={{ fontSize: 13 }}>Click <strong>New Mission</strong> in the banner above to create one.</span>
      </div>
    );
  }

  return (
    <div>
      {stats && (
        <div className="v26-stats-bar">
          <div className="v26-stat-card">
            <div className="v26-stat-card-num" style={{ color: '#711b45' }}>{stats.overallCompletion}%</div>
            <div className="v26-stat-card-label">Overall Progress</div>
          </div>
          <div className="v26-stat-card">
            <div className="v26-stat-card-num" style={{ color: '#10b981' }}>{stats.completed}</div>
            <div className="v26-stat-card-label">Completed</div>
          </div>
          <div className="v26-stat-card">
            <div className="v26-stat-card-num" style={{ color: '#a8336b' }}>{stats.inProgress}</div>
            <div className="v26-stat-card-label">In Progress</div>
          </div>
          <div className="v26-stat-card">
            <div className="v26-stat-card-num" style={{ color: '#ef4444' }}>{stats.atRisk}</div>
            <div className="v26-stat-card-label">At Risk</div>
          </div>
          <div className="v26-stat-card">
            <div className="v26-stat-card-num" style={{ color: '#94a3b8' }}>{stats.notStarted}</div>
            <div className="v26-stat-card-label">Not Started</div>
          </div>
          <div className="v26-stat-card">
            <div className="v26-stat-card-num" style={{ color: '#711b45' }}>
              {stats.goalsCompleted}/{stats.goalsTotal}
            </div>
            <div className="v26-stat-card-label">Goals</div>
          </div>
        </div>
      )}

      <div className="v26-mission-grid">
        {missions.map(mission => {
          const color = STATUS_COLORS[mission.status] || 'gray';
          const pct = mission.completionPercentage;
          const isAutoAtRisk = mission.autoRisk === 'High';
          const locked = !!mission.isLocked;
          return (
            <div key={mission._id} className={`v26-mission-card v26-mission-card--${color}${locked ? ' v26-mission-card--locked' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setRoadmapMissionId(mission._id)}
              title={locked ? 'Locked — read-only. Click to view the roadmap.' : 'Click to open roadmap'}>
              <div className="v26-card-top">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="v26-card-order">#{mission.order}</span>
                  <span className="v26-badge v26-badge--primary" style={{ fontSize: 10 }}>{selectedYear}</span>
                </div>
                <div className="v26-card-badges">
                  {locked && (
                    <span className="v26-badge v26-badge--gray" title={mission.lockReason || 'Closed phase — frozen and hidden from users'}>
                      🔒 Locked
                    </span>
                  )}
                  <span className={`v26-badge v26-badge--${color}`}>{mission.status}</span>
                  <span className={`v26-badge v26-badge--${RISK_COLORS[mission.riskLevel]}`}>{mission.riskLevel} Risk</span>
                  {isAutoAtRisk && mission.status !== 'At Risk' && (
                    <span className="v26-badge v26-badge--red" title="Auto-detected risk based on velocity">⚠ Auto</span>
                  )}
                </div>
              </div>
              <div className="v26-card-name">{mission.name}</div>
              {mission.basedOn && (
                <div style={{ fontSize: 11, color: '#64748b', marginTop: -4, marginBottom: 6 }}>
                  ↳ continues <strong>#{mission.basedOn.order} {mission.basedOn.name}</strong>
                </div>
              )}
              <div className="v26-card-team">
                {mission.head && (
                  <span className="v26-team-chip" style={{ fontSize: 11, padding: '2px 8px', background: '#fef3c7', color: '#92400e' }}>
                    👑 {mission.head.fullName || mission.head}
                  </span>
                )}
                {(mission.team || []).map(m => (
                  <span key={m._id || m} className="v26-team-chip" style={{ fontSize: 11, padding: '2px 8px' }}>
                    {m.fullName || m}
                  </span>
                ))}
              </div>
              <div className="v26-progress-row">
                <div className="v26-progress-bar-wrap">
                  <div className="v26-progress-bar" style={{ width: `${pct}%`, background: ProgressColor(pct) }} />
                </div>
                <span className="v26-progress-pct">{pct}%</span>
              </div>
              <div className="v26-card-footer">
                <div className="v26-deadline">
                  Target: <strong>{new Date(mission.targetDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</strong>
                </div>
                <CountdownLabel date={mission.targetDate} status={mission.status} />
              </div>
              {mission.remarks && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#64748b', fontStyle: 'italic', borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                  {mission.remarks}
                </div>
              )}
              {locked && (
                <div style={{ marginTop: 10, fontSize: 11, color: '#475569', background: '#f1f5f9', borderRadius: 6, padding: '6px 9px' }}>
                  🔒 Closed phase — frozen and hidden from all non-admin users.
                  {mission.lockReason && <><br />{mission.lockReason}</>}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                {!locked && (
                  <button className="v26-btn v26-btn--secondary v26-btn--sm" onClick={() => setEditMission(mission)}>
                    <HiOutlinePencil size={13} /> Edit
                  </button>
                )}
                <button className="v26-btn v26-btn--primary v26-btn--sm" onClick={() => setRoadmapMissionId(mission._id)}>
                  {locked ? 'View Roadmap →' : 'Open Roadmap →'}
                </button>
                {isAdmin && (
                  <button
                    className="v26-btn v26-btn--secondary v26-btn--sm"
                    disabled={lockBusyId === mission._id}
                    onClick={() => toggleLock(mission)}
                    title={locked ? 'Unlock this mission' : 'Lock this mission — closes the phase'}
                  >
                    {lockBusyId === mission._id ? '…' : (locked ? '🔓 Unlock' : '🔒 Lock')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editMission && (
        <EditMissionModal
          mission={editMission}
          systemUsers={systemUsers}
          onClose={() => setEditMission(null)}
          onSave={() => { setEditMission(null); onMissionUpdate(); }}
        />
      )}
      {roadmapMissionId && (
        <RoadmapDrawer
          missionId={roadmapMissionId}
          systemUsers={systemUsers}
          onClose={() => setRoadmapMissionId(null)}
          onAnyChange={onMissionUpdate}
          onRollup={onRollup}
          refreshKey={roadmapRefreshKey}
        />
      )}
    </div>
  );
};

export default MissionDashboard;
